// src/screens/customer/customerPannel/orderdetails/Orders.jsx

import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Filter, Package, Palette, ImageIcon } from "lucide-react";
import "../../../../assets/css/customer/customerPannel/orderdetails/Orders.css";
import AppContext from "../../../../context/AppContext";
import axios from "axios";

// ─────────────────────────────────────────────────────────────
// CONSTANTS — matched with admin statuses
// ─────────────────────────────────────────────────────────────
const STATUS_META = {
  pending: { color: "#b45309", bg: "rgba(234,179,8,0.1)" },
  "in progress": { color: "#2563eb", bg: "rgba(37,99,235,0.1)" },
  completed: { color: "#16a34a", bg: "rgba(22,163,74,0.1)" },
  canceled: { color: "#dc2626", bg: "rgba(220,38,38,0.1)" },
};

const ORDER_TYPE_CONFIG = {
  normal: { label: "Normal", icon: Package, className: "ord-type--normal" },
  custom: {
    label: "Customized",
    icon: Palette,
    className: "ord-type--custom",
  },
};

const FILTER_OPTIONS = [
  { value: "All", label: "All Orders" },
  { value: "pending", label: "Pending" },
  { value: "in progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "canceled", label: "Canceled" },
];

// ─────────────────────────────────────────────────────────────
// HELPER — capitalize first letter of status from API
// ─────────────────────────────────────────────────────────────
const capitalizeStatus = (status) => {
  if (!status) return "pending";
  return status
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// ─────────────────────────────────────────────────────────────
// TYPE BADGE COMPONENT
// ─────────────────────────────────────────────────────────────
const TypeBadge = ({ type }) => {
  const config = ORDER_TYPE_CONFIG[type];
  if (!config) return null;
  const Icon = config.icon;
  return (
    <span className={`ord-type-badge ${config.className}`}>
      <Icon size={10} />
      {config.label}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
const Orders = () => {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");
  const [orders, setOrders] = useState([]);
  const { baseUrl, authToken, baseUrlForFile, loggedInCustomer } =
    useContext(AppContext);

  const filtered = orders.filter((o) => {
    const matchSearch = String(o.id)
      .toLowerCase()
      .includes(search.toLowerCase());
    const status = capitalizeStatus(o.status);
    const matchFilter = filter === "All" || status === filter;
    return matchSearch && matchFilter;
  });

  useEffect(() => {
    viewOrders();
  }, []);

  const viewOrders = () => {
    const formData = new FormData();
    formData.append("adcustomer_id", loggedInCustomer?.id);

    axios
      .post(`${baseUrl}/customer/order/viewOrders`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        console.log(response.data);
        setOrders(response.data.orders);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  return (
    <div className="ord-page">
      {/* ── Header ── */}
      <div className="ord-header">
        <div>
          <h1 className="ord-title">My Orders</h1>
          <p className="ord-subtitle">View and track all your orders</p>
        </div>
        <div className="ord-total-badge">
          <span className="ord-total-num">{orders.length}</span>
          <span className="ord-total-lbl">Total Orders</span>
        </div>
      </div>

      {/* ── Controls ── */}
      <div className="ord-controls">
        <div className="ord-search-wrap">
          <Search size={16} strokeWidth={1.8} className="ord-search-icon" />
          <input
            type="text"
            className="ord-search-input"
            placeholder="Search by Order ID or item name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="ord-filter-wrap">
          <Filter size={15} strokeWidth={1.8} className="ord-filter-icon" />
          <select
            className="ord-filter-select"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            {FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Content ── */}
      {filtered.length === 0 ? (
        <div className="ord-empty">
          <Package
            size={48}
            strokeWidth={1.2}
            color="#D67E43"
            style={{ opacity: 0.5 }}
          />
          <h3>No Orders Found</h3>
          <p>
            {search || filter !== "All"
              ? "Try adjusting your search or filter"
              : "You haven't placed any orders yet"}
          </p>
          <Link to="/products" className="ord-shop-btn">
            Start Shopping
          </Link>
        </div>
      ) : (
        <>
          {/* ── Desktop Table ── */}
          <div className="ord-table-wrap">
            <table className="ord-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Date</th>
                  <th>Items</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Amount</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => {
                  const status = capitalizeStatus(o.status);
                  const m = STATUS_META[status] || {
                    color: "#888",
                    bg: "#eee",
                  };
                  return (
                    <tr key={o.id} className="ord-row">
                      <td className="ord-id">{o.id}</td>
                      <td>
                        {new Date(o.created_at).toLocaleDateString("en-GB")}
                      </td>
                      <td>
                        <span className="ord-item-name">
                          {o.adcart?.[0]?.adproduct?.name}
                        </span>
                        {o.adcart?.length > 1 && (
                          <span className="ord-item-more">
                            +{o.adcart.length - 1} more
                          </span>
                        )}
                      </td>
                      <td>
                        <TypeBadge type={o.type} />
                      </td>
                      <td>
                        <span
                          className="ord-status-badge"
                          style={{ color: m.color, background: m.bg }}
                        >
                          {status}
                        </span>
                      </td>
                      <td className="ord-amount">
                        PKR {o.total_price?.toLocaleString()}
                      </td>
                      <td>
                        <Link
                          to={`/customer/orders/${o.id}`}
                          className="ord-view-btn"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ── Mobile Cards ── */}
          <div className="ord-cards-wrap">
            {filtered.map((o) => {
              const status = capitalizeStatus(o.status);
              const m = STATUS_META[status] || { color: "#888", bg: "#eee" };
              return (
                <div key={o.id} className="ord-card">
                  <img
                    src={`${baseUrlForFile}${o.adcart?.[0]?.adproduct?.productimages?.[0]?.image_path}`}
                    alt={o.adcart?.[0]?.adproduct?.name}
                    className="ord-card-img"
                  />
                  <div className="ord-card-body">
                    <div className="ord-card-top">
                      <span className="ord-id">{o.id}</span>
                      <span
                        className="ord-status-badge"
                        style={{ color: m.color, background: m.bg }}
                      >
                        {status}
                      </span>
                    </div>
                    {/* Type badge on mobile */}
                    <TypeBadge type={o.type || "normal"} />
                    <p className="ord-item-name">
                      {o.adcart?.[0]?.adproduct?.name}
                      {o.adcart?.length > 1 ? ` +${o.adcart.length - 1}` : ""}
                    </p>
                    <div className="ord-card-footer">
                      <span className="ord-amount">
                        PKR {o.total_price?.toLocaleString()}
                      </span>
                      <span className="ord-card-date">
                        {new Date(o.created_at).toLocaleDateString("en-GB")}
                      </span>
                      <Link
                        to={`/customer/orders/${o.id}`}
                        className="ord-view-btn"
                      >
                        View
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <p className="ord-results-txt">
            Showing <strong>{filtered.length}</strong> of{" "}
            <strong>{orders.length}</strong> orders
          </p>
        </>
      )}
    </div>
  );
};

export default Orders;
